<script lang="ts">
  // Changes view per INTERFACE-SPECIFICATION.md §8.
  //
  // Tabs:
  //   /changes/my-drafts                      My Drafts
  //   /changes/pending-review                 Pending Review queue
  //   /changes/pending-review/{pr_number}     Review interface
  //   /changes/changes-requested              Proposals with outstanding REQUEST_CHANGES
  //   /changes/changes-requested/{pr_number}  Review interface (changes-requested entry)
  //   /changes/approved-queued                Approved & queued
  //   /changes/recently-completed             Recently completed
  //
  // The Review interface (Section 8.2) is the load-bearing affordance
  // for acceptance criterion 3.

  import { onMount } from 'svelte';
  import { route, navigate } from '../lib/router.svelte';
  import { identity } from '../lib/identity.svelte';
  import MyDraftsList from '../components/MyDraftsList.svelte';
  import ProposalsList from '../components/ProposalsList.svelte';
  import ReviewInterface from '../components/ReviewInterface.svelte';
  import RevisionEditor from '../components/RevisionEditor.svelte';
  import ApprovedQueuedList from '../components/ApprovedQueuedList.svelte';

  const tab = $derived(route.current.params.tab ?? 'my-drafts');
  const referenceNumber = $derived(parseReference(route.current.params.reference));
  const action = $derived(route.current.params.action);

  function parseReference(ref: string | undefined): number | null {
    if (!ref) return null;
    const n = Number(ref);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function setTab(target: string) {
    navigate(`/changes/${target}`);
  }

  function pickPending(prNumber: number) {
    navigate(`/changes/pending-review/${prNumber}`);
  }
  function pickChangesRequested(prNumber: number) {
    navigate(`/changes/changes-requested/${prNumber}`);
  }
  function pickMerged(prNumber: number) {
    navigate(`/changes/recently-completed/${prNumber}`);
  }
  // [Revise] from a Pending Review or Changes Requested card
  // (INTERFACE-SPECIFICATION.md §8.1) opens the Revise interface
  // (§8.4) in the same tab the card lives in. The route already
  // mounts RevisionEditor; this just preserves tab context.
  function reviseFromPending(prNumber: number) {
    navigate(`/changes/pending-review/${prNumber}/revise`);
  }
  function reviseFromChangesRequested(prNumber: number) {
    navigate(`/changes/changes-requested/${prNumber}/revise`);
  }
</script>

<section>
  <nav class="changes-tabs" aria-label="Changes tabs">
    <button type="button" class:is-active={tab === 'my-drafts'} onclick={() => setTab('my-drafts')}>My drafts</button>
    {#if identity.affordances.canReviewAnything || identity.affordances.canRollback}
      <button type="button" class:is-active={tab === 'pending-review'} onclick={() => setTab('pending-review')}>Pending review</button>
    {/if}
    <button type="button" class:is-active={tab === 'changes-requested'} onclick={() => setTab('changes-requested')}>Changes requested</button>
    <button type="button" class:is-active={tab === 'approved-queued'} onclick={() => setTab('approved-queued')}>Approved &amp; queued</button>
    <button type="button" class:is-active={tab === 'recently-completed'} onclick={() => setTab('recently-completed')}>Recently completed</button>
  </nav>

  {#if tab === 'my-drafts'}
    <MyDraftsList />
  {:else if tab === 'pending-review'}
    {#if referenceNumber !== null && action === 'revise'}
      <RevisionEditor prNumber={referenceNumber} />
    {:else if referenceNumber !== null}
      <ReviewInterface prNumber={referenceNumber} />
    {:else}
      <ProposalsList status="open" onPick={pickPending} onRevise={reviseFromPending} />
    {/if}
  {:else if tab === 'changes-requested'}
    {#if referenceNumber !== null && action === 'revise'}
      <RevisionEditor prNumber={referenceNumber} />
    {:else if referenceNumber !== null}
      <ReviewInterface prNumber={referenceNumber} />
    {:else}
      <ProposalsList status="changes_requested" onPick={pickChangesRequested} onRevise={reviseFromChangesRequested} />
    {/if}
  {:else if tab === 'approved-queued'}
    <ApprovedQueuedList />
  {:else if tab === 'recently-completed'}
    {#if referenceNumber !== null}
      <ReviewInterface prNumber={referenceNumber} />
    {:else}
      <!-- 'completed' includes both merged and closed-without-merge so the
           Recently completed list shows everything that's reached a
           terminal PR state. Each card is badged by its own status. -->
      <ProposalsList status="completed" onPick={pickMerged} />
    {/if}
  {:else}
    <p class="muted">Unknown Changes tab: {tab}</p>
  {/if}
</section>

<style>
  .changes-tabs {
    display: flex;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
    border-bottom: 1px solid var(--surface-line-soft);
  }
  .changes-tabs button {
    border: 0;
    background: transparent;
    padding: var(--space-2) var(--space-3);
    font-size: var(--type-sm);
    color: var(--surface-muted);
    border-bottom: 2px solid transparent;
    cursor: pointer;
  }
  .changes-tabs button.is-active {
    color: var(--surface-fg);
    border-bottom-color: var(--surface-fg);
    font-weight: 500;
  }
  /* On phone width the five tabs do not fit on one row; scroll
   * horizontally and snap each tab cleanly so the user can swipe
   * through them without a hamburger or dropdown indirection. */
  @media (max-width: 768px) {
    .changes-tabs {
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      flex-wrap: nowrap;
    }
    .changes-tabs button {
      white-space: nowrap;
      scroll-snap-align: start;
      min-height: var(--touch-target-min);
    }
  }
</style>
